import { Link } from 'react-router-dom';
import type {
  ActivityOverviewDimension,
  ActivityOverviewDomain,
  ActivityOverviewStatusFilter,
} from '@sparkapp/shared-types';
import { cn } from '@/lib/utils';
import { activityListHref } from './activityListHref';

interface PreferenceDomainHeatmapProps {
  status: ActivityOverviewStatusFilter;
  preferences: ActivityOverviewDimension[]; // rows; row total = activityCount
  domains: ActivityOverviewDomain[]; // columns; cell = domains[c].preferenceCounts[r]
  onSelectDomain?: (key: string) => void; // column header click opens the domain detail
}

const HEAT_CLASSES = [
  'bg-pine-l1 text-pine-ink',
  'bg-pine-l2 text-pine-ink',
  'bg-pine-l3 text-pine-ink',
  'bg-pine-l4 text-white',
  'bg-pine-l5 text-white',
] as const;

const ZERO_CLASS = 'bg-amber-50 text-amber-800 ring-1 ring-inset ring-amber-300';

/** Quantise against the largest cell so the scale adapts to the filter. */
function heatClass(count: number, max: number): string {
  if (count === 0) return ZERO_CLASS;
  return HEAT_CLASSES[Math.min(4, Math.ceil((count / max) * 5) - 1)];
}

/** Integer count range covered by each heat step; steps with no possible integer are dropped. */
function legendSteps(max: number) {
  return HEAT_CLASSES.map((className, i) => ({
    className,
    lo: Math.floor((i * max) / 5) + 1,
    hi: Math.floor(((i + 1) * max) / 5),
  })).filter((step) => step.lo <= step.hi);
}

const STATUS_WORD: Record<ActivityOverviewStatusFilter, string> = {
  draft: 'draft',
  published: 'published',
  unpublished: 'unpublished',
  archived: 'archived',
  all: 'non-archived',
};

const headerCell = 'border border-pine-line bg-pine-hover-item px-3 py-2 text-pine-ink font-semibold';

/**
 * Preference × Domain heatmap. Each activity has exactly one primary domain, so
 * cells partition the preference's activities and rows sum to the Total column.
 * Zero cells are amber: a child with that preference working in that domain gets
 * no content under the active status filter.
 */
export function PreferenceDomainHeatmap({
  status,
  preferences,
  domains,
  onSelectDomain,
}: PreferenceDomainHeatmapProps) {
  const max = Math.max(
    0,
    ...domains.flatMap((d) => d.preferenceCounts.map((c) => c.activityCount))
  );

  if (preferences.length === 0) {
    return <p className="text-sm text-pine-ink-soft">No preferences configured.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead>
            <tr>
              <th className={cn(headerCell, 'sticky left-0 z-10 text-left')}>
                Preference \ Domain
              </th>
              {domains.map((domain) => {
                const dot = (
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: domain.style?.strokeColor ?? 'var(--pine-pine)' }}
                  />
                );
                return (
                  <th key={domain.id} className={cn(headerCell, 'text-left')}>
                    {onSelectDomain ? (
                      <button
                        type="button"
                        onClick={() => onSelectDomain(domain.key)}
                        title={`Open ${domain.title}`}
                        className="flex items-center gap-2 whitespace-nowrap hover:underline"
                      >
                        {dot}
                        {domain.title}
                      </button>
                    ) : (
                      <span className="flex items-center gap-2 whitespace-nowrap">
                        {dot}
                        {domain.title}
                      </span>
                    )}
                  </th>
                );
              })}
              <th className={cn(headerCell, 'text-right')}>Total</th>
            </tr>
          </thead>
          <tbody>
            {preferences.map((preference, r) => {
              const isEmptyRow = preference.activityCount === 0;
              return (
                <tr key={preference.id}>
                  <th
                    className={cn(
                      headerCell,
                      'sticky left-0 z-10 text-left whitespace-nowrap',
                      isEmptyRow && 'bg-amber-50 text-amber-800'
                    )}
                  >
                    <span className="flex items-center gap-2">
                      {preference.title}
                      {isEmptyRow && (
                        <span className="rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                          No activities
                        </span>
                      )}
                    </span>
                  </th>
                  {domains.map((domain) => {
                    const count = domain.preferenceCounts[r]?.activityCount ?? 0;
                    return (
                      <td key={domain.id} className="border border-pine-line p-0 text-center tabular-nums">
                        <Link
                          to={activityListHref(status, {
                            domainId: domain.id,
                            preferenceId: preference.id,
                          })}
                          title={`${count} ${STATUS_WORD[status]} ${
                            count === 1 ? 'activity' : 'activities'
                          } in ${domain.title} tagged ${preference.title}`}
                          className={cn(
                            'block px-3 py-2 hover:opacity-80',
                            heatClass(count, max),
                            count > 0 && 'font-semibold'
                          )}
                        >
                          {count === 0 ? '·' : count}
                        </Link>
                      </td>
                    );
                  })}
                  <td
                    className={cn(
                      'border border-pine-line px-3 py-2 text-right font-semibold tabular-nums',
                      isEmptyRow ? 'text-amber-800' : 'text-pine-ink'
                    )}
                  >
                    {preference.activityCount}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <th className={cn(headerCell, 'sticky left-0 z-10 text-left')}>Activities</th>
              {domains.map((domain) => (
                <td key={domain.id} className={cn(headerCell, 'text-center tabular-nums')}>
                  {domain.activityCount}
                </td>
              ))}
              <td className={cn(headerCell, 'text-right')} />
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-caption text-pine-ink-soft">
        {legendSteps(max).map((step) => (
          <span key={step.className} className="flex items-center gap-1.5">
            <span className={cn('inline-block h-3 w-5 rounded-sm', step.className)} />
            {step.lo === step.hi ? step.lo : `${step.lo}–${step.hi}`}
          </span>
        ))}
        <span className="flex items-center gap-1.5">
          <span className={cn('inline-block h-3 w-5 rounded-sm', ZERO_CLASS)} />
          no activities (gap)
        </span>
      </div>

      <p className="text-caption text-pine-ink-soft">
        Each activity sits in exactly one primary domain, so rows add up to the total. A child who
        likes <em>X</em> and is working on <em>Y</em> gets nothing where a cell is empty.
      </p>
    </div>
  );
}
